import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent } from "react";
import { isInteractiveDragTarget } from "../lib/drag";

export function useInteractiveDragSuppression() {
  const [draggable, setDraggable] = useState(true);
  const releaseListenersRef = useRef<(() => void) | undefined>(undefined);
  const stopSuppressingDrag = useCallback(() => {
    releaseListenersRef.current?.();
    releaseListenersRef.current = undefined;
    setDraggable(true);
  }, []);
  const suppressDrag = useCallback(() => {
    releaseListenersRef.current?.();
    window.addEventListener("pointerup", stopSuppressingDrag, true);
    window.addEventListener("pointercancel", stopSuppressingDrag, true);
    window.addEventListener("blur", stopSuppressingDrag);
    releaseListenersRef.current = () => {
      window.removeEventListener("pointerup", stopSuppressingDrag, true);
      window.removeEventListener("pointercancel", stopSuppressingDrag, true);
      window.removeEventListener("blur", stopSuppressingDrag);
    };
    setDraggable(false);
  }, [stopSuppressingDrag]);
  const onPointerDownCapture = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (isInteractiveDragTarget(event.target)) suppressDrag();
    },
    [suppressDrag],
  );

  useEffect(
    () => () => {
      releaseListenersRef.current?.();
    },
    [],
  );

  return { draggable, onPointerDownCapture, stopSuppressingDrag };
}
